import React, { useState, useEffect, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import { Tabs, Tab, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import AADService from "../funcionesAuth.js";
import GraficaRiesgo from "./GraficoComponente.js";
import * as echarts from "echarts";
import CircularProgress from "@mui/material/CircularProgress";

const PieChartComponent = ({ data }) => {
  const [selectedOption, setSelectedOption] = useState("total");
  const idCompania = localStorage.getItem("idcompania");
  const isCompaniaEspecial = idCompania === "1" || idCompania === "5";

  const getAvailableOptions = () => {
    const allOptions = [
      { value: "total", label: "Total" },
      { value: "principalesmodulos", label: "Principales-Modulos" },
      { value: "esquemas", label: "Esquemas" },
      { value: "nubes", label: "Nubes" },
      { value: "bia", label: "BIA" },
      { value: "sox", label: "SOX" }
    ];

    // Primero aplicar el filtro por compañía
    let filteredByCompany;
    if (isCompaniaEspecial) {
      filteredByCompany = allOptions;
    } else {
      filteredByCompany = allOptions.filter(option =>
        option.value !== "total" && option.value !== "principalesmodulos"
      );
    }

    // Luego filtrar solo las opciones que tienen datos en el JSON
    const dataOptions = data || {};
    const optionsWithData = filteredByCompany.filter(option => {
      const optionData = dataOptions[option.value];
      return optionData && Array.isArray(optionData) && optionData.length > 0;
    });

    return optionsWithData;
  };

  const availableOptions = getAvailableOptions();

  useEffect(() => {
    if (!isCompaniaEspecial && (selectedOption === "total" || selectedOption === "principalesmodulos")) {
      if (availableOptions.length > 0) {
        setSelectedOption(availableOptions[0].value);
      }
    } else if (availableOptions.length > 0 && !availableOptions.find(opt => opt.value === selectedOption)) {
      // Si la opción seleccionada no está en las opciones disponibles, cambiar a la primera disponible
      setSelectedOption(availableOptions[0].value);
    }
  }, [isCompaniaEspecial, selectedOption, availableOptions]);

  useEffect(() => {
    const chartDom = document.getElementById("chart");
    const myChart = echarts.init(chartDom);

    // Obtener datos disponibles para la opción seleccionada
    const dataOptions = data || {};
    const currentData = dataOptions[selectedOption] || [];

    const option = {
      legend: {
        top: "top",
      },
      tooltip: {
        trigger: "item",
      },
      toolbox: {
        feature: {
          saveAsImage: {
            show: true,
            title: 'Guardar como imagen'
          }
        }
      },
      series: [
        {
          type: "pie",
          radius: "50%",
          center: ["50%", "50%"],
          data: currentData, // Solo mostrar los datos que existen
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
          label: {
            formatter: "{b}: {c} ({d}%)",
          },
        },
      ],
    };

    myChart.setOption(option);

    return () => {
      myChart.dispose();
    };
  }, [selectedOption, data]);

  return (
    <div>
      {availableOptions.length > 0 ? (
        <>
          <div style={{ marginBottom: "20px" }}>
            <select
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
            >
              {availableOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div id="chart" style={{ width: "600px", height: "400px" }}></div>
        </>
      ) : (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "600px",
          height: "400px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "5px",
          textAlign: "center"
        }}>
          <div>
            <h5 style={{ color: "#666", marginBottom: "10px" }}>Gráfico de Aplicaciones</h5>
            <p style={{ color: "#999", margin: 0 }}>No hay datos disponibles</p>
          </div>
        </div>
      )}
    </div>
  );
};

const ResumenComponentes = () => {
  const [DataResumen, setDataResumen] = useState([]);
  const AzureADService = new AADService();
  const token = AzureADService.getToken();
  const [loading, setLoading] = React.useState(false);
  const [datosRiesgo, setDatosRiesgo] = useState([]);
  const [tablaC, setTablaC] = useState([]);
  const [tablaI, setTablaI] = useState([]);
  const [tablaD, setTablaD] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  // Función para formatear números con separadores de miles (comas) y decimales (puntos)
  const formatearNumero = (numero) => {
    if (numero === null || numero === undefined || numero === '') {
      return '';
    }

    // Convertir a número si es string
    const num = typeof numero === 'string' ? parseFloat(numero) : Number(numero);

    // Verificar si es un número válido
    if (isNaN(num)) {
      return '';
    }

    // Formatear con máximo 2 decimales, usando comas para miles y puntos para decimales
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Función para verificar si un objeto está vacío o no tiene datos válidos
  const tieneDatos = (objeto) => {
    if (!objeto) return false;
    if (Array.isArray(objeto)) return objeto.length > 0;
    if (typeof objeto === 'object') {
      return Object.keys(objeto).length > 0 &&
        Object.values(objeto).some(valor =>
          Array.isArray(valor) ? valor.length > 0 : valor
        );
    }
    return false;
  };

  // Función para verificar si hay algún dato disponible en el resumen
  const hayDatosDisponibles = () => {
    return tieneDatos(tablaC) ||
      tieneDatos(tablaI) ||
      tieneDatos(tablaD) ||
      (DataResumen.tabla_apps && DataResumen.tabla_apps.length > 0) ||
      (DataResumen.grafico_apps && Object.keys(DataResumen.grafico_apps).some(key =>
        Array.isArray(DataResumen.grafico_apps[key]) && DataResumen.grafico_apps[key].length > 0
      ));
  };

  useEffect(() => {
    const obtenerResumen = async () => {
      try {
        setLoading(true);
        const result = await fetch(
          process.env.REACT_APP_API_URL +
          "activoInformacion/resumenPerfilRiesgo/" +
          localStorage.getItem("idcompania") + "/",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );

        if (result.status >= 200 && result.status <= 300) {
          let datResumen = await result.json();
          setDataResumen(datResumen);
          setTablaC(datResumen.tabla_c || []);
          setTablaI(datResumen.tabla_i || []);
          setTablaD(datResumen.tabla_d || []);
        } else if (result.status >= 400 && result.status <= 500) {
          console.error('Error al obtener datos:', result.status);
          return result.status;
        }
      } catch (error) {
        console.error('Error en la petición:', error);
      } finally {
        setLoading(false);
      }
    };

    obtenerResumen();
  }, []);

  const obtenerTabla = (key) => ({
    tabla_c: tablaC,
    tabla_i: tablaI,
    tabla_d: tablaD,
  }[key] || []);

  const pestañas = [
    { key: "tabla_c", titulo: "Confidencialidad" },
    { key: "tabla_i", titulo: "Integridad" },
    { key: "tabla_d", titulo: "Disponibilidad" },
  ];

  const handleChange = (event, newIndex) => setTabIndex(newIndex);
  const { key, titulo } = pestañas[tabIndex];
  const datos = obtenerTabla(key) || [];

  // Verificar si hay datos para mostrar la tabla_apps
  const hayDatosTablaApps = DataResumen.tabla_apps && DataResumen.tabla_apps.length > 0;

  // Verificar si hay datos para mostrar el gráfico
  const hayDatosGrafico = DataResumen.grafico_apps &&
    Object.keys(DataResumen.grafico_apps).some(key =>
      Array.isArray(DataResumen.grafico_apps[key]) && DataResumen.grafico_apps[key].length > 0
    );

  // Verificar si hay datos para mostrar la sección de tabla y gráfico (al menos uno de los dos)
  const hayDatosSeccionApps = hayDatosTablaApps || hayDatosGrafico;

  return (
    <div>
      <br />
      <br />
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <CircularProgress />
        </div>
      ) : !hayDatosDisponibles() ? (
        // Mensaje cuando no hay datos disponibles para nada
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          textAlign: "center",
          flexDirection: "column"
        }}>
          <h3 style={{
            color: "#666",
            marginBottom: "20px",
            fontWeight: "300"
          }}>
            Lo sentimos, no tenemos datos disponibles para esta compañía
          </h3>
        </div>
      ) : (
        <>
          {/* Solo mostrar la sección de tabla y gráfico si hay datos en al menos uno de ellos */}
          {hayDatosSeccionApps && (
            <Row>
              {hayDatosTablaApps && (
                <Col sm={6} xs={12}>
                  <TableContainer>
                    <Table stickyHeader size={"medium"} maxHeightBody="55vh">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            style={{ backgroundColor: "#000000", color: "white" }}
                          >
                            Clase
                          </TableCell>
                          <TableCell
                            style={{ backgroundColor: "#000000", color: "white" }}
                          >
                            Principales
                          </TableCell>
                          <TableCell
                            style={{ backgroundColor: "#000000", color: "white" }}
                          >
                            Modulos
                          </TableCell>
                          <TableCell
                            style={{ backgroundColor: "#000000", color: "white" }}
                          >
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {DataResumen.tabla_apps.map((row, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell style={{ backgroundColor: "#F2F2F2" }}>
                                {row.clase}
                              </TableCell>
                              <TableCell style={{ backgroundColor: "#F2F2F2" }}>
                                {formatearNumero(row.principales)}
                              </TableCell>
                              <TableCell style={{ backgroundColor: "#F2F2F2" }}>
                                {formatearNumero(row.modulos)}
                              </TableCell>
                              <TableCell style={{ backgroundColor: "#F2F2F2" }}>
                                {formatearNumero(row.total)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Col>
              )}
              {hayDatosGrafico && (
                <Col
                  sm={hayDatosTablaApps ? 6 : 12}
                  xs={12}
                  className={hayDatosTablaApps ? "" : "d-flex justify-content-center"}
                >
                  <PieChartComponent data={DataResumen.grafico_apps} />
                </Col>
              )}

            </Row>
          )}

          <GraficaRiesgo datos={DataResumen} />

          {/* Solo mostrar las tablas de riesgo si hay datos en alguna de ellas */}
          {(tieneDatos(tablaC) || tieneDatos(tablaI) || tieneDatos(tablaD)) && (
            <Box>
              <div style={{ marginTop: 10 }}>
                <h5>Top 10 aplicativos por riesgo - {titulo}</h5>
              </div>

              <Tabs
                value={tabIndex}
                onChange={handleChange}
                centered
                sx={{
                  width: 'fit-content',
                  backgroundColor: "#000",
                  "& .MuiTab-root": {
                    color: "#aaa",
                    minWidth: 160,
                    mx: 4,
                    textAlign: "center",
                  },
                  "& .Mui-selected": {
                    color: "#fff",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#fff",
                  },
                }}
              >
                {pestañas.map((tab) => (
                  <Tab
                    key={tab.key}
                    label={tab.titulo}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                ))}
              </Tabs>

              <TableContainer style={{ marginTop: "20px" }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>Id Evaluación</TableCell>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>Id Activo</TableCell>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>Nombre</TableCell>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>Analista</TableCell>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>P50</TableCell>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>P95</TableCell>
                      <TableCell style={{ backgroundColor: "#000000", color: "white" }}>P99</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {datos.length > 0 ? (
                      datos.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{row.idevaluacion}</TableCell>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{row.idactivo}</TableCell>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{row.nombre}</TableCell>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{row.analista}</TableCell>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{formatearNumero(row.exposicioninherentep50)}</TableCell>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{formatearNumero(row.exposicioninherentep95)}</TableCell>
                          <TableCell style={{ backgroundColor: "#F2F2F2" }}>{formatearNumero(row.exposicioninherentep99)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                          No hay datos disponibles para {titulo.toLowerCase()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default ResumenComponentes;